import OpenAI from 'openai';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

// Token estimation constants
const APPROX_CHARS_PER_TOKEN = 4; // Rough estimate: 1 token ≈ 4 characters
const MAX_CONTEXT_TOKENS = 12000; // Conservative limit for gpt-4o (16k context)
const RESERVED_TOKENS_FOR_RESPONSE = 4096; // Reserve space for response
const MAX_INPUT_TOKENS = MAX_CONTEXT_TOKENS - RESERVED_TOKENS_FOR_RESPONSE; // ~8000 tokens for input

/**
 * Estimate token count for a message or string
 * @param {string|object} content - Message content or message object
 * @returns {number} Estimated token count
 */
function estimateTokens(content) {
  const text = typeof content === 'string' ? content : content?.content || '';
  return Math.ceil(text?.length / APPROX_CHARS_PER_TOKEN);
}

/**
 * Trim conversation messages to fit within token limits
 * Keeps system message, most recent messages, and maintains conversation flow
 * @param {Array} messages - Array of conversation messages
 * @param {number} maxTokens - Maximum tokens allowed
 * @returns {Array} Trimmed messages array
 */
function trimMessagesToFitContext(messages, maxTokens = MAX_INPUT_TOKENS) {
  if (!messages || messages?.length === 0) return [];
  
  // Calculate total tokens
  let totalTokens = 0;
  const messagesWithTokens = messages?.map(msg => {
    const tokens = estimateTokens(msg);
    totalTokens += tokens;
    return { ...msg, estimatedTokens: tokens };
  });
  
  // If under limit, return as-is
  if (totalTokens <= maxTokens) {
    return messages;
  }
  
  console.log(`⚠️ Token limit approaching: ${totalTokens} tokens (limit: ${maxTokens}). Trimming conversation...`);
  
  // Strategy: Keep most recent messages and maintain user-assistant pairs
  const trimmedMessages = [];
  let currentTokenCount = 0;
  
  // Start from the end (most recent) and work backwards
  for (let i = messagesWithTokens?.length - 1; i >= 0; i--) {
    const msg = messagesWithTokens?.[i];
    const potentialTotal = currentTokenCount + msg?.estimatedTokens;
    
    // If adding this message would exceed limit, stop
    if (potentialTotal > maxTokens) {
      break;
    }
    
    // Add message to beginning of trimmed array
    trimmedMessages?.unshift(messages?.[i]);
    currentTokenCount = potentialTotal;
  }
  
  // Ensure we have at least the last user message if possible
  if (trimmedMessages?.length === 0 && messages?.length > 0) {
    const lastMessage = messages?.[messages?.length - 1];
    if (lastMessage?.role === 'user' && lastMessage?.estimatedTokens <= maxTokens) {
      trimmedMessages?.push(lastMessage);
    }
  }
  
  const removedCount = messages?.length - trimmedMessages?.length;
  if (removedCount > 0) {
    console.log(`✂️ Trimmed ${removedCount} older messages to fit context window. Keeping ${trimmedMessages?.length} recent messages.`);
  }
  
  return trimmedMessages;
}

/**
 * Stream AI response with auto-continue functionality - ChatGPT-like behavior
 * Uses GPT-4o with streaming, temperature 0.7, high max_tokens, fallback to GPT-4o-mini
 * @param {Array} messages - Array of conversation messages
 * @param {Object} settings - User configuration settings
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {boolean} hasVisionContent - Flag indicating if request contains images (for vision model selection)
 * @returns {Promise<{content: string, totalCycles: number, model: string}>} Complete response with metadata
 */
export async function streamAIResponseWithAutoContinue(
  messages, 
  settings = {}, 
  onChunk, 
  onProgress,
  signal = null,
  hasVisionContent = false
) {
  const MAX_CYCLES = 10; // Safety limit for auto-continue
  
  // ✅ ALWAYS USE gpt-4o as primary model for maximum quality (text and vision)
  const PRIMARY_MODEL = 'gpt-4o';
  const FALLBACK_MODEL = 'gpt-4o-mini';
  
  let fullResponse = '';
  let cycleCount = 0;
  let shouldContinue = true;
  let currentModel = PRIMARY_MODEL;

  try {
    while (shouldContinue && cycleCount < MAX_CYCLES) {
      cycleCount++;
      
      // Check for cancellation
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      onProgress?.({
        cycle: cycleCount,
        maxCycles: MAX_CYCLES,
        status: `Gerando resposta (ciclo ${cycleCount}/${MAX_CYCLES})...`,
        model: currentModel
      });

      // Prepare messages for this cycle
      let currentMessages = [...messages];
      if (cycleCount > 1 && fullResponse) {
        // Add previous response and continue prompt for subsequent cycles
        currentMessages?.push({ role: 'assistant', content: fullResponse });
        currentMessages?.push({ role: 'user', content: 'continue' });
      }
      
      // 🆕 TRIM MESSAGES TO FIT CONTEXT WINDOW
      currentMessages = trimMessagesToFitContext(currentMessages);

      try {
        const stream = await openai?.chat?.completions?.create({
          model: currentModel,
          messages: [
            { role: 'system', content: SYSTEM_PROMPTS?.['ai_text_generator'] },
            ...currentMessages
          ],
          temperature: 0.7, // ChatGPT-like creativity
          max_tokens: 4096, // High token limit for complete responses
          stream: true,
        }, { signal });

        let cycleResponse = '';
        let finishReason = null;

        for await (const chunk of stream) {
          // Check for cancellation during streaming
          if (signal?.aborted) {
            throw new Error('Geração cancelada pelo usuário');
          }

          const content = chunk?.choices?.[0]?.delta?.content || '';
          const finish = chunk?.choices?.[0]?.finish_reason;
          
          if (content) {
            cycleResponse += content;
            fullResponse += content;
            onChunk?.(content);
          }
          
          if (finish) {
            finishReason = finish;
          }
        }

        // Check if we should continue based on finish reason
        shouldContinue = finishReason === 'length' && cycleCount < MAX_CYCLES;
        
        if (!shouldContinue || cycleCount >= MAX_CYCLES) {
          onProgress?.({
            cycle: cycleCount,
            maxCycles: MAX_CYCLES,
            status: finishReason === 'length' && cycleCount >= MAX_CYCLES ?'Resposta completa (limite de segurança atingido)' :'Resposta completa',
            model: currentModel
          });
        }

      } catch (modelError) {
        // Fallback to gpt-4o-mini if gpt-4o fails
        if (currentModel === PRIMARY_MODEL && cycleCount === 1) {
          console.log('Tentando modelo alternativo gpt-4o-mini...');
          currentModel = FALLBACK_MODEL;
          cycleCount--; // Retry with same cycle count
          continue;
        }
        throw modelError;
      }
    }

    return {
      content: fullResponse,
      totalCycles: cycleCount,
      limitReached: cycleCount >= MAX_CYCLES,
      model: currentModel
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    console.error('Error in streaming with auto-continue:', error);
    
    if (error?.response?.status === 401) {
      throw new Error('Erro de autenticação. Verifique sua chave da API OpenAI.');
    } else if (error?.response?.status === 429) {
      throw new Error('Limite de requisições atingido. Aguarde alguns instantes e tente novamente.');
    } else if (error?.response?.status === 500 || error?.response?.status === 503) {
      throw new Error('Serviço OpenAI temporariamente indisponível. Tente novamente.');
    }
    
    throw new Error('Falha ao gerar resposta. Tente novamente.');
  }
}

/**
 * Simple streaming AI response (original function maintained for compatibility)
 */
export async function streamAIResponse(userMessage, settings, fileContent = '', onChunk) {
  const messages = [
    { role: 'user', content: fileContent ? `${fileContent}\n\n${userMessage}` : userMessage }
  ];
  
  const result = await streamAIResponseWithAutoContinue(messages, settings, onChunk);
  return result?.content;
}

/**
 * Enhanced PDF export with better formatting and bubble content handling
 */
export function exportToPDF(messages, settings = {}) {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Title
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Chat Inteligente - Histórico de Conversas', margin, yPosition);
    yPosition += 10;

    // Date and settings
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    const dateStr = new Date()?.toLocaleString('pt-BR');
    pdf?.text(`Data: ${dateStr}`, margin, yPosition);
    yPosition += 15;

    // Process each message bubble
    pdf?.setFontSize(11);
    messages?.forEach((msg, index) => {
      const isUser = msg?.role === 'user';
      const roleLabel = isUser ? 'USUÁRIO' : 'ASSISTENTE';
      
      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        pdf?.addPage();
        yPosition = margin;
      }

      // Role header with styling
      pdf?.setFont('helvetica', 'bold');
      pdf?.setTextColor(isUser ? 0 : 102, isUser ? 102 : 0, isUser ? 204 : 102);
      pdf?.text(`${roleLabel}:`, margin, yPosition);
      yPosition += 8;

      // Message content with proper text wrapping
      pdf?.setFont('helvetica', 'normal');
      pdf?.setTextColor(0, 0, 0);
      
      const lines = pdf?.splitTextToSize(msg?.content, maxWidth);
      lines?.forEach(line => {
        if (yPosition > pageHeight - 20) {
          pdf?.addPage();
          yPosition = margin;
        }
        pdf?.text(line, margin, yPosition);
        yPosition += 5;
      });
      
      yPosition += 10; // Space between messages
    });

    // Generate filename with timestamp
    const timestamp = new Date()?.toISOString()?.slice(0, 19)?.replace(/:/g, '-')?.replace('T', '_');
    
    pdf?.save(`chat-inteligente_${timestamp}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Erro ao exportar PDF');
  }
}

/**
 * Export individual bubble to PDF
 */
export function exportBubbleToPDF(messageContent, messageRole = 'assistant') {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Title
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Chat Inteligente - Resposta Individual', margin, yPosition);
    yPosition += 15;

    // Date
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    const dateStr = new Date()?.toLocaleString('pt-BR');
    pdf?.text(`Data: ${dateStr}`, margin, yPosition);
    yPosition += 15;

    // Content with proper formatting
    pdf?.setFontSize(11);
    const lines = pdf?.splitTextToSize(messageContent, maxWidth);
    lines?.forEach(line => {
      if (yPosition > pageHeight - 20) {
        pdf?.addPage();
        yPosition = margin;
      }
      pdf?.text(line, margin, yPosition);
      yPosition += 6;
    });

    const timestamp = new Date()?.toISOString()?.slice(0, 19)?.replace(/:/g, '-')?.replace('T', '_');
    
    pdf?.save(`chat-inteligente_${timestamp}.pdf`);
  } catch (error) {
    console.error('Error exporting bubble to PDF:', error);
    throw new Error('Erro ao exportar PDF');
  }
}

/**
 * Export individual bubble to DOCX
 */
export function exportBubbleToDOCX(messageContent, messageRole = 'assistant') {
  try {
    // Create document sections
    const sections = [];
    
    // Title paragraph
    sections?.push(
      new Paragraph({
        text: 'Chat Inteligente - Resposta Individual',
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 }
      })
    );
    
    // Date paragraph
    const dateStr = new Date()?.toLocaleString('pt-BR');
    sections?.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Data: ${dateStr}`,
            size: 20,
            color: '666666'
          })
        ],
        spacing: { after: 400 }
      })
    );
    
    // Content paragraphs - split by newlines
    const contentLines = messageContent?.split('\n') || [];
    contentLines?.forEach(line => {
      sections?.push(
        new Paragraph({
          children: [
            new TextRun({
              text: line || ' ',
              size: 22
            })
          ],
          spacing: { after: 120 }
        })
      );
    });
    
    // Create document
    const doc = new Document({
      sections: [{
        properties: {},
        children: sections
      }]
    });
    
    // Generate and download
    Packer?.toBlob(doc)?.then(blob => {
      const timestamp = new Date()?.toISOString()?.slice(0, 19)?.replace(/:/g, '-')?.replace('T', '_');
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat-inteligente_${timestamp}.docx`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    });
    
  } catch (error) {
    console.error('Error exporting bubble to DOCX:', error);
    throw new Error('Erro ao exportar DOCX');
  }
}

/**
 * Export full conversation to DOCX
 */
export function exportToDOCX(messages, settings = {}) {
  try {
    const sections = [];
    
    // Title
    sections?.push(
      new Paragraph({
        text: 'Chat Inteligente - Histórico de Conversas',
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      })
    );
    
    // Date
    const dateStr = new Date()?.toLocaleString('pt-BR');
    sections?.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Data: ${dateStr}`,
            size: 20,
            color: '666666'
          })
        ],
        spacing: { after: 400 }
      })
    );
    
    // Process each message
    messages?.forEach((msg, index) => {
      const isUser = msg?.role === 'user';
      const roleLabel = isUser ? 'USUÁRIO' : 'ASSISTENTE';
      
      // Role header
      sections?.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${roleLabel}:`,
              bold: true,
              size: 24,
              color: isUser ? '0066CC' : '006666'
            })
          ],
          spacing: { before: index > 0 ? 300 : 200, after: 120 }
        })
      );
      
      // Message content
      const contentLines = msg?.content?.split('\n') || [];
      contentLines?.forEach(line => {
        sections?.push(
          new Paragraph({
            children: [
              new TextRun({
                text: line || ' ',
                size: 22
              })
            ],
            spacing: { after: 100 }
          })
        );
      });
    });
    
    // Create document
    const doc = new Document({
      sections: [{
        properties: {},
        children: sections
      }]
    });
    
    // Generate and download
    Packer?.toBlob(doc)?.then(blob => {
      const timestamp = new Date()?.toISOString()?.slice(0, 19)?.replace(/:/g, '-')?.replace('T', '_');
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat-inteligente_${timestamp}.docx`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    });
    
  } catch (error) {
    console.error('Error exporting to DOCX:', error);
    throw new Error('Erro ao exportar DOCX');
  }
}

/**
 * Check OpenAI API connection status
 */
export async function checkAPIConnection() {
  try {
    const response = await openai?.models?.list();
    return { connected: true, models: response?.data };
  } catch (error) {
    console.error('API connection error:', error);
    return { connected: false, error: error?.message };
  }
}

/**
 * Process uploaded file content (maintained for compatibility)
 */
export async function processFileUpload(file) {
  return new Promise((resolve, reject) => {
    if (file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          type: 'txt',
          content: e.target.result,
          name: file.name
        });
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo TXT'));
      reader.readAsText(file);
    } else if (file.type === 'application/pdf') {
      resolve({
        type: 'pdf',
        content: `[PDF] ${file.name} - Conteúdo do arquivo PDF foi reconhecido.`,
        name: file.name
      });
    } else {
      reject(new Error('Formato de arquivo não suportado. Use apenas TXT ou PDF.'));
    }
  });
}